import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, X, Send, Loader2, Bot, User, 
  Sparkles, ArrowRight, Mail, Phone, Building,
  CheckCircle, Clock, Zap
} from 'lucide-react';

// =============================================================================
// COLORS
// =============================================================================

const c = {
  bg: '#0a0a0a',
  bgChat: '#0d0d0d',
  bgMessage: 'rgba(255, 255, 255, 0.06)',
  bgUser: 'linear-gradient(135deg, #007AFF 0%, #0055CC 100%)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.7)',
  textSubtle: 'rgba(255, 255, 255, 0.5)',
  border: 'rgba(255, 255, 255, 0.08)',
  accent: '#007AFF',
  success: '#22c55e',
};

// =============================================================================
// GENERATE SESSION ID
// =============================================================================

function getSessionId() {
  let sessionId = sessionStorage.getItem('chat_session_id');
  if (!sessionId) {
    sessionId = 'chat_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    sessionStorage.setItem('chat_session_id', sessionId);
  }
  return sessionId;
}

// =============================================================================
// QUICK ACTION BUTTONS
// =============================================================================

const QUICK_ACTIONS = [
  { label: 'Hva koster det?', icon: '💰' },
  { label: 'Hvor lang tid tar det?', icon: '⏱️' },
  { label: 'Fortell om prosessen', icon: '🚀' },
];

// =============================================================================
// TYPING INDICATOR
// =============================================================================

function TypingIndicator() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '8px 0',
    }}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          animate={{ y: [0, -5, 0] }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            delay: i * 0.15,
          }}
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: c.textSubtle,
          }}
        />
      ))}
    </div>
  );
}

// =============================================================================
// LEAD CAPTURE FORM (inline in chat)
// =============================================================================

function LeadCaptureCard({ onSubmit, onSkip }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) return;
    setLoading(true);
    
    try {
      await onSubmit(formData);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          borderRadius: '16px',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <CheckCircle size={32} color={c.success} style={{ marginBottom: '12px' }} />
        <div style={{ fontSize: '15px', color: c.text, fontWeight: '500', marginBottom: '4px' }}>
          Takk, {formData.name.split(' ')[0]}!
        </div>
        <div style={{ fontSize: '13px', color: c.textMuted }}>
          Vi tar kontakt innen 24 timer.
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'linear-gradient(135deg, rgba(0,122,255,0.1) 0%, rgba(0,85,204,0.05) 100%)',
        border: '1px solid rgba(0,122,255,0.2)',
        borderRadius: '16px',
        padding: '20px',
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        marginBottom: '12px' 
      }}>
        <Sparkles size={16} color={c.accent} />
        <span style={{ fontSize: '14px', fontWeight: '500', color: c.text }}>
          Vil du at vi tar kontakt?
        </span>
      </div>
      
      <p style={{ 
        fontSize: '13px', 
        color: c.textMuted, 
        marginBottom: '16px',
        lineHeight: 1.5,
      }}>
        Legg igjen kontaktinfo så ringer vi deg for en uforpliktende prat.
      </p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Ditt navn *"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          style={{
            padding: '12px 14px',
            fontSize: '14px',
            background: 'rgba(0,0,0,0.3)',
            border: `1px solid ${c.border}`,
            borderRadius: '10px',
            color: c.text,
            outline: 'none',
          }}
        />
        <input
          type="email"
          placeholder="E-post *"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          style={{
            padding: '12px 14px',
            fontSize: '14px',
            background: 'rgba(0,0,0,0.3)',
            border: `1px solid ${c.border}`,
            borderRadius: '10px',
            color: c.text,
            outline: 'none',
          }}
        />
        <input
          type="tel"
          placeholder="Telefon (valgfritt)"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          style={{
            padding: '12px 14px',
            fontSize: '14px',
            background: 'rgba(0,0,0,0.3)',
            border: `1px solid ${c.border}`,
            borderRadius: '10px',
            color: c.text,
            outline: 'none',
          }}
        />
      </div>
      
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={handleSubmit}
          disabled={!formData.name || !formData.email || loading}
          style={{
            flex: 1,
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
            background: formData.name && formData.email ? c.accent : 'rgba(255,255,255,0.1)',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            cursor: formData.name && formData.email ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          {loading ? <Loader2 size={16} className="spin" /> : <>Send <ArrowRight size={16} /></>}
        </button>
        <button
          onClick={onSkip}
          style={{
            padding: '12px 16px',
            fontSize: '13px',
            background: 'transparent',
            color: c.textSubtle,
            border: `1px solid ${c.border}`,
            borderRadius: '10px',
            cursor: 'pointer',
          }}
        >
          Senere
        </button>
      </div>
    </motion.div>
  );
}

// =============================================================================
// MESSAGE COMPONENT
// =============================================================================

function Message({ message, isLast }) {
  const isUser = message.role === 'user';
  const isSystem = message.type === 'system';
  const isLeadCapture = message.type === 'lead_capture';
  
  if (isLeadCapture) {
    return (
      <div style={{ marginBottom: '12px' }}>
        <LeadCaptureCard 
          onSubmit={message.onSubmit} 
          onSkip={message.onSkip} 
        />
      </div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.2 }}
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '12px',
      }}
    >
      {!isUser && (
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #007AFF 0%, #0055CC 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '10px',
          flexShrink: 0,
        }}>
          <Bot size={14} color="#fff" />
        </div>
      )}
      
      <div style={{
        maxWidth: '80%',
        padding: '12px 16px',
        borderRadius: isUser 
          ? '18px 18px 4px 18px' 
          : '18px 18px 18px 4px',
        background: isUser ? c.bgUser : c.bgMessage,
        color: c.text,
        fontSize: '14px',
        lineHeight: 1.6,
        boxShadow: isUser ? '0 2px 10px rgba(0,122,255,0.2)' : 'none',
      }}>
        {message.content}
      </div>
    </motion.div>
  );
}

// =============================================================================
// MAIN CHAT COMPONENT
// =============================================================================

export default function ChatBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [messageCount, setMessageCount] = useState(0);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [sessionId] = useState(getSessionId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initial welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'Hei! 👋 Jeg er Autonomous AI. Jeg kan svare på spørsmål om hvordan vi bygger apper og plattformer med AI – 5x raskere og 50% billigere enn tradisjonelt. Hva lurer du på?'
      }]);
    }
  }, [isOpen, messages.length]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Show lead capture after 3 messages
  useEffect(() => {
    if (messageCount >= 3 && !leadCaptured) {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          type: 'lead_capture',
          onSubmit: handleLeadSubmit,
          onSkip: handleLeadSkip,
        }]);
      }, 1000);
    }
  }, [messageCount, leadCaptured]);

  const handleLeadSubmit = async (formData) => {
    try {
      // Get UTM params
      const utmParams = JSON.parse(sessionStorage.getItem('utm_params') || '{}');
      
      await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          ...utmParams,
          message: `[Fra AI Chat] Samtale-ID: ${sessionId}`,
          page_url: window.location.href,
        }),
      });
      
      setLeadCaptured(true);
      
      // Add confirmation message
      setTimeout(() => {
        setMessages(prev => prev.filter(m => m.type !== 'lead_capture'));
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Supert, ${formData.name.split(' ')[0]}! 🎉 Vi har notert deg og tar kontakt snart. Er det noe mer du lurer på i mellomtiden?`
        }]);
      }, 500);
    } catch (err) {
      console.error('Lead submission error:', err);
    }
  };

  const handleLeadSkip = () => {
    setMessages(prev => prev.filter(m => m.type !== 'lead_capture'));
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Ingen problem! Bare si fra hvis du ombestemmer deg. Er det noe annet jeg kan hjelpe med?'
    }]);
    setLeadCaptured(true);
  };

  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;

    const userMessage = text.trim();
    setInput('');
    setShowQuickActions(false);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    setMessageCount(prev => prev + 1);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        }),
      });

      if (!response.ok) throw new Error('Failed');

      // Always read as text first, then parse
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      setMessageCount(prev => prev + 1);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Beklager, noe gikk galt. Du kan ta kontakt direkte på martin@autonomous.no eller prøve igjen.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        .pulse { animation: pulse 2s ease-in-out infinite; }
      `}</style>

      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="pulse"
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              width: '64px',
              height: '64px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #007AFF 0%, #0055CC 100%)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 24px rgba(0, 122, 255, 0.4)',
              zIndex: 9998,
            }}
          >
            <MessageCircle size={28} color="#fff" />
            
            {/* Notification badge */}
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: 'spring' }}
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                background: c.success,
                color: '#fff',
                fontSize: '10px',
                fontWeight: '600',
                padding: '4px 8px',
                borderRadius: '10px',
                border: '2px solid #0a0a0a',
              }}
            >
              AI
            </motion.span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              width: '400px',
              maxWidth: 'calc(100vw - 32px)',
              height: '600px',
              maxHeight: 'calc(100vh - 100px)',
              background: c.bgChat,
              borderRadius: '24px',
              border: `1px solid ${c.border}`,
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              zIndex: 9999,
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 20px',
              borderBottom: `1px solid ${c.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 100%)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #007AFF 0%, #0055CC 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,122,255,0.3)',
                }}>
                  <Sparkles size={22} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: c.text }}>
                    Autonomous AI
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: c.success, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '5px' 
                  }}>
                    <span style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: c.success,
                    }} />
                    Svarer vanligvis med en gang
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px',
                  cursor: 'pointer',
                  color: c.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px 16px',
            }}>
              {messages.map((msg, i) => (
                <Message key={i} message={msg} isLast={i === messages.length - 1} />
              ))}
              
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #007AFF 0%, #0055CC 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '10px',
                  }}>
                    <Bot size={14} color="#fff" />
                  </div>
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '18px 18px 18px 4px',
                    background: c.bgMessage,
                  }}>
                    <TypingIndicator />
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            <AnimatePresence>
              {showQuickActions && messages.length <= 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  style={{
                    padding: '0 16px 12px',
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                  }}
                >
                  {QUICK_ACTIONS.map((action, i) => (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      onClick={() => sendMessage(action.label)}
                      style={{
                        padding: '10px 14px',
                        fontSize: '13px',
                        background: 'rgba(255,255,255,0.05)',
                        border: `1px solid ${c.border}`,
                        borderRadius: '20px',
                        color: c.textMuted,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s',
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.1)';
                        e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.05)';
                        e.target.style.borderColor = c.border;
                      }}
                    >
                      <span>{action.icon}</span> {action.label}
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input */}
            <div style={{
              padding: '16px',
              borderTop: `1px solid ${c.border}`,
              background: 'linear-gradient(0deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
            }}>
              <div style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-end',
              }}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Skriv en melding..."
                  rows={1}
                  style={{
                    flex: 1,
                    padding: '14px 18px',
                    fontSize: '14px',
                    background: 'rgba(255,255,255,0.05)',
                    border: `1px solid ${c.border}`,
                    borderRadius: '16px',
                    color: c.text,
                    resize: 'none',
                    outline: 'none',
                    fontFamily: 'inherit',
                    maxHeight: '120px',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                  onBlur={(e) => e.target.style.borderColor = c.border}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '14px',
                    background: input.trim() && !loading 
                      ? 'linear-gradient(135deg, #007AFF 0%, #0055CC 100%)' 
                      : 'rgba(255,255,255,0.05)',
                    border: 'none',
                    cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    boxShadow: input.trim() && !loading ? '0 4px 12px rgba(0,122,255,0.3)' : 'none',
                  }}
                >
                  <Send size={18} color={input.trim() && !loading ? '#fff' : c.textSubtle} />
                </button>
              </div>
              
              {/* Powered by */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                marginTop: '12px',
              }}>
                <Zap size={12} color={c.textSubtle} />
                <span style={{ fontSize: '11px', color: c.textSubtle }}>
                  Drevet av GPT-5.2
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
